import {Button, Card, DatePicker, Form, Input, message, Popconfirm, Select, Switch} from 'antd'
import React, {useEffect, useState} from 'react'
import {instance as eventsService} from '../../services/PlantEventsJournalService'
import {ImageUpload} from '../plantForm/ImageUpload'
import {Link, useRevalidator, useSubmit} from 'react-router-dom'
import {useIntl} from 'react-intl'
import dayjs from 'dayjs'
import {dateFormat} from '../../entities/todoList'
import utc from 'dayjs/plugin/utc'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import {
  ToDoTypeMetadata,
  JournalRecord,
  getJournalEventTypeInfo,
  JournalEventType,
  PredefinedEventTypeId,
  EventTypeMetadata,
  CustomJournalEventType,
  CustomType,
} from '../../entities/journalRecord'
import {ToDoTypeFormSelector} from '../todos/ToDoTypeFormSelector'
import {instance as journalService} from '../../services/JournalService'
import {markPlantForRevalidation} from '../../routes/plantRevalidator'
import {DateRangePicker} from '../ui/dateRangePicker/DateRangePicker'

dayjs.extend(utc)
dayjs.extend(localizedFormat)

const validateMessages = {
  required: '${label} is required!',
}

const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e
  }
  return e?.fileList
}

type JournalEventFormProps = {
  onFormClose: () => void
  currentEvent?: JournalRecord
  plantId?: number
  onPlantEventListUpdated?: () => void
  editable?: boolean
  setLoading?: (isLoading: boolean) => void
  userJournalEventTypes: CustomJournalEventType[]
  currentPlant?: {id: number; name: string}
}

export function JournalEventForm({
  onFormClose,
  currentEvent,
  plantId,
  onPlantEventListUpdated = () => {},
  editable = true,
  setLoading = () => {},
  userJournalEventTypes,
  currentPlant,
}: JournalEventFormProps) {
  const [eventForm] = Form.useForm()
  const revalidator = useRevalidator()
  const intl = useIntl()
  const [messageApi, contextHolder] = message.useMessage()
  const files = Form.useWatch('files', eventForm)
  const newFiles = Form.useWatch('newFiles', eventForm)
  const submit = useSubmit()
  const [isRange, setIsRange] = useState(false)

  useEffect(() => {
    ;(async () => {
      const fieldValues = !currentEvent
        ? {}
        : {
            ...currentEvent,
            startDateUtc: currentEvent?.startDateUtc
              ? dayjs(currentEvent.startDateUtc, dateFormat)
              : null,
            endDateUtc: currentEvent?.endDateUtc
              ? dayjs(currentEvent.endDateUtc, dateFormat)
              : null,
            actualCompletionDateUtc: currentEvent?.actualCompletionDateUtc
              ? dayjs(currentEvent.actualCompletionDateUtc, dateFormat)
              : null,
            files: [],
            chosenType:
              currentEvent.type.type === 'custom'
                ? currentEvent.type.customJournalEventType
                : {
                    id: null,
                    name: currentEvent.type.type,
                    icon: getJournalEventTypeInfo(currentEvent.type).icon,
                  },
          }
      eventForm.setFieldsValue(fieldValues)
      // const signedPhotoURLs = await eventsService.getPlantEventImageURLs(currentEvent?.photos)
      // eventForm.setFieldsValue({
      //   ...fieldValues,
      //   files: currentEvent?.photos?.map((photoName, index) => ({
      //     uid: index,
      //     photoName: photoName,
      //     status: 'done',
      //     //url: signedPhotoURLs.urls[index],
      //   })),
      // })
    })()
  }, [currentEvent])

  const onCreateUserEventType = async (name: string, icon: string) => {
    const creatingResult = await journalService.createUserJournalEventType(name, icon)
    if (creatingResult.success) {
      markPlantForRevalidation()
      revalidator.revalidate()
    }
    return creatingResult
  }

  const onEditUserEventType = async (newType: CustomType) => {
    const editingResult = await journalService.editUserJournalEventType(newType)
    if (editingResult.success) {
      markPlantForRevalidation()
      revalidator.revalidate()
    }
    return editingResult
  }

  const onDeleteUserEventType = async (deletedTypeId: number) => {
    const deletingResult = await journalService.deleteUserJournalEventType(deletedTypeId)
    if (deletingResult.success) {
      markPlantForRevalidation()
      revalidator.revalidate()
    }
  }

  return (
    <div>
      {contextHolder}
      <Card title={intl.formatMessage({id: 'eventInfo'})} className="plant-event-form">
        <Form
          form={eventForm}
          labelCol={{span: 24}}
          wrapperCol={{span: 24}}
          autoComplete="off"
          layout="vertical"
          validateMessages={validateMessages}
          onFinish={async (formState) => {
            let result: {success: boolean; error?: string}
            const timeEvent: Omit<JournalRecord, 'id'> = {
              comment: formState.comment,
              type: formState.chosenType.id
                ? {type: 'custom', customJournalEventType: formState.chosenType}
                : {type: formState.chosenType.name},
              plant: {id: plantId, name: ''},
              actualCompletionDateUtc: (dayjs(formState.actualCompletionDateUtc) as any).utc(true),
              startDateUtc: isRange
                ? formState.dateRange?.[0]?.format(dateFormat)
                : formState.date?.format(dateFormat),
              endDateUtc: isRange ? formState.dateRange?.[1]?.format(dateFormat) : null,
              //endDateUtc: undefined,
            }
            if (currentEvent) {
              const {...existingPlantEvent} = currentEvent
              result = await journalService.editJournalEvent(
                {...existingPlantEvent, ...timeEvent},
                formState.newFiles,
                formState.files?.map((file, index) => file.photoName)
              )
            } else {
              result = await journalService.createJournalEvent(timeEvent, formState.newFiles)
            }
            if (result.success) {
              revalidator.revalidate()
              onPlantEventListUpdated()
              submit(currentPlant ? {id: String(currentPlant.id)} : null, {
                method: 'post',
              })
            } else {
              messageApi.open({
                type: 'error',
                content: result.error,
              })
            }
            onFormClose()
          }}
        >
          {currentEvent?.plant?.id && (
            <div>
              {intl.formatMessage({id: 'plant'})} :
              <Link to={`/journal/${currentEvent.id}/plants/${currentEvent.plant.id}`}>
                {currentEvent.plant.name}
              </Link>
            </div>
          )}

          <Form.Item
            name="chosenType"
            valuePropName="chosenType"
            label={intl.formatMessage({id: 'eventType'})}
          >
            {editable ? (
              <ToDoTypeFormSelector
                commonTypes={Object.keys(EventTypeMetadata).map((type: PredefinedEventTypeId) => ({
                  id: null,
                  name: type,
                  icon: EventTypeMetadata[type]?.icon,
                }))}
                userTypes={userJournalEventTypes.map((type) => ({
                  id: type.customJournalEventType.id,
                  name: type.customJournalEventType.name,
                  icon: type.customJournalEventType.icon,
                }))}
                createNewItem={onCreateUserEventType}
                editable={editable}
                deleteUserType={onDeleteUserEventType}
                editType={onEditUserEventType}
                isAllowClear={false}
              />
            ) : (
              currentEvent.type.type && (
                <>
                  <div>
                    {` ${getJournalEventTypeInfo(currentEvent.type).icon} ${intl.formatMessage({
                      id: `eventType-${getJournalEventTypeInfo(currentEvent.type).name}`,
                    })}`}
                  </div>
                </>
              )
            )}
          </Form.Item>

          {editable ? (
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <Form.Item label={intl.formatMessage({id: 'dateRange'})}>
                <Switch checked={isRange} onChange={setIsRange} />
              </Form.Item>
              {isRange ? (
                <Form.Item name="dateRange" label={intl.formatMessage({id: 'dateRange'})}>
                  <DateRangePicker />
                </Form.Item>
              ) : (
                <Form.Item name="date" label={intl.formatMessage({id: 'date'})}>
                  <DatePicker />
                </Form.Item>
              )}
            </div>
          ) : (
            currentEvent.startDateUtc && (
              <div>
                {intl.formatMessage({id: 'date'})}:{` ${currentEvent.startDateUtc}`}
                {currentEvent.endDateUtc && ` - ${currentEvent.endDateUtc}`}
              </div>
            )
          )}

          <Form.Item
            name="actualCompletionDateUtc"
            label={intl.formatMessage({id: 'eventEndDate'})}
            rules={[{required: true}]}
          >
            {editable ? (
              <DatePicker disabled={!editable} />
            ) : (
              <div>{`${currentEvent.actualCompletionDateUtc}`}</div>
            )}
          </Form.Item>

          <Form.Item name="comment" label={intl.formatMessage({id: 'comment'})}>
            {editable ? (
              <Input.TextArea disabled={!editable} />
            ) : (
              <div>{`${currentEvent.comment ? currentEvent.comment : ''}`}</div>
            )}
          </Form.Item>
          {/* {editable && (
            <Form.Item
              label={intl.formatMessage({id: 'eventPhotos'})}
              name="files"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <ImageUpload addable={false} disabled={!editable} />
            </Form.Item>
          )}
          {editable && (
            <Form.Item
              label={intl.formatMessage({id: 'uploadPhotos'})}
              name="newFiles"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <ImageUpload
                addable={true}
                disabled={(files?.length || 0) + (newFiles?.length || 0) > 1}
              />
            </Form.Item>
          )} */}
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            {editable && (
              <Form.Item>
                <Button
                  onClick={async () => {
                    onFormClose()
                  }}
                >
                  {intl.formatMessage({id: 'cancel'})}
                </Button>
              </Form.Item>
            )}
            {/* <Form.Item>
            <Button
              onClick={async () => {
                onFormClose()
              }}
            >
              {editable
                ? intl.formatMessage({id: 'cancel'})
                : intl.formatMessage({id: 'closeEvent'})}
            </Button>
          </Form.Item> */}
            {currentEvent ? (
              <Form.Item>
                <Popconfirm
                  title={intl.formatMessage({id: 'deleteEvent'})}
                  description={`${intl.formatMessage({
                    id: 'areYouSureYouWantToDeleteThisEvent',
                  })} ${intl.formatMessage({id: 'thisOperationIsIrreversible'})}`}
                  onConfirm={async () => {
                    await journalService.deleteJournalEvent(currentEvent)
                    onPlantEventListUpdated()
                    onFormClose()
                    //submit(null, {method: 'delete'})
                    submit(currentPlant ? {id: String(currentPlant.id)} : null, {method: 'post'})
                  }}
                  onCancel={() => {}}
                  okText={intl.formatMessage({id: 'yes'})}
                  cancelText={intl.formatMessage({id: 'no'})}
                >
                  <Button>{intl.formatMessage({id: 'deleteEvent'})}</Button>
                </Popconfirm>
              </Form.Item>
            ) : null}
            {editable && (
              <Form.Item>
                <Button htmlType="submit" type="primary">
                  {currentEvent
                    ? intl.formatMessage({id: 'saveEvent'})
                    : intl.formatMessage({id: 'addEvent'})}
                </Button>
              </Form.Item>
            )}
          </div>
        </Form>
      </Card>
    </div>
  )
}
